// Черновата на НОВ документ, докато листът е отворен: auto-save в localStorage
// (400 ms — резерв при прекъсната връзка) И на сървъра (1,5 s — вижда се от
// колегите и от друго устройство), heartbeat на заключването на 30 s и
// освобождаване при изход. Общ за доставка, изписване, прехвърляне и
// инвентаризация — листът подава типа и снимката си.
//
// Своя чернова → `PUT /drafts/mine?type=…`; чужда (поета от колега) → `PUT
// /drafts/{id}`, докато държим заключването. 409 = колега я е поел → спираме
// записа на сървъра и казваме на листа (`lockLostBy`). 404 = стар BE без
// чернови (или без този тип) → работим само с localStorage.
import { useCallback, useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  deleteDraft,
  heartbeatDraft,
  lockHolderOf,
  releaseDraft,
  saveDraft,
  saveMyDraft,
  type DraftStatus,
  type DraftType,
} from "../../../../api/inventoryDrafts";
import { draftBodyOf, type DraftSummaryFields, type LocalDraftStore } from "./draftModel";

const LOCAL_DELAY_MS = 400;
const SERVER_DELAY_MS = 1500;
const HEARTBEAT_MS = 30_000;

export type DraftSnapshot<P> = { payload: P; summary: DraftSummaryFields };

type Params<P> = {
  type: DraftType;
  enabled: boolean; // само нов документ — редакцията няма чернова
  // сървърната чернова, от която листът е отворен (своя или поета)
  initialDraftId: number | null;
  own: boolean;
  // четена лениво (след паузата) — от вече пребоядисаното състояние
  snapshot: () => DraftSnapshot<P>;
  local: LocalDraftStore<P>;
};

const isNotFound = (error: unknown) =>
  (error as { response?: { status?: number } })?.response?.status === 404;

export const useDraftSync = <P>({ type, enabled, initialDraftId, own, snapshot, local }: Params<P>) => {
  const queryClient = useQueryClient();
  const [draftId, setDraftId] = useState<number | null>(initialDraftId);
  const [lockLostBy, setLockLostBy] = useState<string | null>(null);
  const draftIdRef = useRef(draftId);
  draftIdRef.current = draftId;
  const snapshotRef = useRef(snapshot);
  snapshotRef.current = snapshot;
  const localRef = useRef(local);
  localRef.current = local;
  const serverOffRef = useRef(false); // 404 или загубено заключване
  const statusRef = useRef<{ status: DraftStatus; problemText: string | null }>({
    status: "draft",
    problemText: null,
  });
  const localTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const serverTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => setDraftId(initialDraftId), [initialDraftId]);

  const persistLocal = useCallback(() => {
    if (own) localRef.current.persist(snapshotRef.current().payload);
  }, [own]);

  const saveServer = useCallback(async () => {
    if (serverOffRef.current) return;
    const { payload, summary } = snapshotRef.current();
    const body = draftBodyOf(payload, summary, statusRef.current);
    try {
      const saved =
        own || draftIdRef.current == null
          ? await saveMyDraft(type, body)
          : await saveDraft(draftIdRef.current, body);
      // началният екран и значката в менюто да видят черновата веднага
      queryClient.invalidateQueries({ queryKey: ["inventory", "drafts"] });
      if (draftIdRef.current == null) queryClient.invalidateQueries({ queryKey: ["inventory", "nav-counts"] });
      setDraftId(saved.id);
    } catch (error) {
      if (isNotFound(error)) {
        serverOffRef.current = true;
        return;
      }
      const holder = lockHolderOf(error);
      if (holder != null) {
        serverOffRef.current = true;
        setLockLostBy(holder || "?");
      }
      // мрежова грешка: следващата промяна ще опита пак (localStorage пази)
    }
  }, [own, type, queryClient]);

  const schedule = useCallback(() => {
    if (!enabled) return;
    if (localTimer.current) clearTimeout(localTimer.current);
    localTimer.current = setTimeout(persistLocal, LOCAL_DELAY_MS);
    if (serverTimer.current) clearTimeout(serverTimer.current);
    serverTimer.current = setTimeout(() => void saveServer(), SERVER_DELAY_MS);
  }, [enabled, persistLocal, saveServer]);

  // Heartbeat: държим заключването, докато листът е отворен
  useEffect(() => {
    if (!enabled || draftId == null) return;
    const beat = () =>
      heartbeatDraft(draftId).catch((error) => {
        const holder = lockHolderOf(error);
        if (holder != null) {
          serverOffRef.current = true;
          setLockLostBy(holder || "?");
        }
      });
    void beat();
    const timer = setInterval(beat, HEARTBEAT_MS);
    return () => {
      clearInterval(timer);
      // освобождаваме, за да не стои „Редактира се" 2 минути след изхода
      if (!serverOffRef.current) void releaseDraft(draftId).catch(() => {});
    };
  }, [enabled, draftId]);

  useEffect(
    () => () => {
      if (localTimer.current) clearTimeout(localTimer.current);
      if (serverTimer.current) clearTimeout(serverTimer.current);
    },
    [],
  );

  // Веднага, без да чакаме debounce-а
  const flush = useCallback(async () => {
    persistLocal();
    if (serverTimer.current) clearTimeout(serverTimer.current);
    await saveServer();
  }, [persistLocal, saveServer]);

  // „Задръж доставката" (проблемни продукти) — веднага, със статус и причина
  const hold = useCallback(
    async (problemText: string) => {
      statusRef.current = { status: "held", problemText };
      await flush();
    },
    [flush],
  );

  // Записан документ / „Изтрий черновата" — черновата изчезва и от двете места.
  // Локалното копие се чисти само ако е СВОЕ: поетата чужда не е в браузъра.
  const remove = useCallback(async () => {
    if (localTimer.current) clearTimeout(localTimer.current);
    if (serverTimer.current) clearTimeout(serverTimer.current);
    serverOffRef.current = true;
    if (own) localRef.current.clear();
    const id = draftIdRef.current;
    if (id != null) await deleteDraft(id).catch(() => {});
    queryClient.invalidateQueries({ queryKey: ["inventory", "drafts"] });
    queryClient.invalidateQueries({ queryKey: ["inventory", "nav-counts"] });
  }, [own, queryClient]);

  // Листът продължи моята сървърна чернова, намерена при отварянето (без id в
  // router state) — заключването и триенето трябва да я знаят отсега
  const adopt = useCallback((id: number) => setDraftId(id), []);

  return { draftId, lockLostBy, schedule, hold, flush, remove, adopt };
};

export type DraftSync = ReturnType<typeof useDraftSync>;
